import type { BlogComment } from "../types";

export interface ThreadedCommentNode {
  comment: BlogComment;
  children: ThreadedCommentNode[];
}

function normalizeDepth(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.trunc(value));
}

export function buildThreadedCommentTree(comments: BlogComment[]): ThreadedCommentNode[] {
  const nodeById: Record<string, ThreadedCommentNode> = {};
  const roots: ThreadedCommentNode[] = [];

  for (const comment of comments) {
    nodeById[comment.id] = {
      comment: {
        ...comment,
        depth: normalizeDepth(comment.depth),
      },
      children: [],
    };
  }

  for (const comment of comments) {
    const node = nodeById[comment.id];
    const parentId = comment.parent_comment_id ?? null;
    if (!parentId) {
      roots.push(node);
      continue;
    }
    const parentNode = nodeById[parentId];
    if (!parentNode) {
      roots.push(node);
      continue;
    }

    const derivedDepth = normalizeDepth(parentNode.comment.depth) + 1;
    if (normalizeDepth(node.comment.depth) < derivedDepth) {
      node.comment = {
        ...node.comment,
        depth: derivedDepth,
      };
    }
    parentNode.children.push(node);
  }

  return roots;
}

export function buildDirectRepliesMap(comments: BlogComment[]): Record<string, BlogComment[]> {
  const replyMap: Record<string, BlogComment[]> = {};
  for (const comment of comments) {
    const parentId = comment.parent_comment_id ?? null;
    if (!parentId) {
      continue;
    }
    if (!replyMap[parentId]) {
      replyMap[parentId] = [];
    }
    replyMap[parentId].push(comment);
  }
  return replyMap;
}
